//
// background-generator.js
// Generative background system for THEY brand colors
//

/**
 * THEY Brand Color Palette
 */
export const palette = {
  blue: '#164656',
  lightBlue: '#7ec7be',
  red: '#EA5837',
  yellow: '#F7B85A',
};

/**
 * Seeded pseudo-random number generator (Mulberry32)
 * Returns consistent "random" numbers for the same seed
 * @param {number} seed - Numeric seed value
 * @returns {function} Random number generator function (0-1)
 */
export function mulberry32(seed) {
  let t = seed >>> 0;
  return function () {
    t += 0x6d2b79f5;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r ^= r + Math.imul(r ^ (r >>> 7), 61 | r);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Converts a string to a numeric seed
 * @param {string} str - Input string
 * @returns {function} Seed factory function
 */
export function hashString(str) {
  let h = 1779033703 ^ str.length;
  for (let i = 0; i < str.length; i++) {
    h = Math.imul(h ^ str.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  return () => {
    h = Math.imul(h ^ (h >>> 16), 2246822507);
    h = Math.imul(h ^ (h >>> 13), 3266489909);
    return (h ^= h >>> 16) >>> 0;
  };
}

/**
 * Clamps a value between min and max
 */
export function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

/**
 * Converts hex color to RGB object
 */
export function hexToRgb(hex) {
  const clean = hex.replace('#', '');
  const bigint = parseInt(clean, 16);
  return {
    r: (bigint >> 16) & 255,
    g: (bigint >> 8) & 255,
    b: bigint & 255,
  };
}

/**
 * Converts hex color to rgba string
 */
export function rgba(hex, alpha) {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Picks a random value from weighted entries
 */
export function pickWeighted(rand, entries) {
  const total = entries.reduce((sum, entry) => sum + entry.weight, 0);
  let roll = rand() * total;
  for (const entry of entries) {
    roll -= entry.weight;
    if (roll <= 0) return entry.value;
  }
  return entries[entries.length - 1].value;
}

/**
 * Generates a single blob configuration
 * @param {function} rand - Random number generator
 * @param {boolean} isAccent - Whether this is an accent color blob
 * @returns {object} Blob configuration
 */
export function generateBlob(rand, isAccent = false, customColors = null) {
  const defaultMainColors = [
    { value: palette.blue, weight: 6 },
    { value: palette.lightBlue, weight: 5 },
    { value: palette.red, weight: 1 },
    { value: palette.yellow, weight: 1 },
  ];
  const defaultAccentColors = [
    { value: palette.red, weight: 3 },
    { value: palette.yellow, weight: 3 },
  ];

  const mainColors = (customColors && customColors.main) || defaultMainColors;
  const accentColors = (customColors && customColors.accent) || defaultAccentColors;

  const color = isAccent ? pickWeighted(rand, accentColors) : pickWeighted(rand, mainColors);
  // Keep blobs away from edges to prevent glow bleeding
  const x = Math.round(10 + rand() * 80);
  const y = Math.round(10 + rand() * 80);
  const size = isAccent ? clamp(20 + rand() * 25, 20, 45) : clamp(28 + rand() * 30, 28, 58);
  const alpha = isAccent ? 0.65 + rand() * 0.3 : 0.7 + rand() * 0.25;

  return { color, x, y, size, alpha };
}

/**
 * Builds a static CSS background-image string with generative blobs
 * @param {string} seedInput - Seed string (e.g., project ID)
 * @param {object} options - Configuration options
 * @returns {string} CSS background-image value
 */
export function buildStaticBackground(seedInput, options = {}) {
  const {
    blobCountMin = 5,
    blobCountMax = 8,
    accentProbability = 0.3,
    glowIntensity = 0.25,
  } = options;

  const seedFactory = hashString(seedInput);
  const rand = mulberry32(seedFactory());
  const layers = [];

  // Generate blobs
  const blobCount = blobCountMin + Math.floor(rand() * (blobCountMax - blobCountMin + 1));
  const accentCount = rand() > accentProbability ? 2 : 1;

  for (let i = 0; i < blobCount; i++) {
    const isAccent = i >= blobCount - accentCount;
    const blob = generateBlob(rand, isAccent);
    layers.push(
      `radial-gradient(circle at ${blob.x}% ${blob.y}%, ${rgba(blob.color, blob.alpha)} 0%, ${rgba(
        blob.color,
        blob.alpha * 0.7,
      )} ${Math.round(blob.size * 0.35)}%, transparent ${Math.round(blob.size)}%)`,
    );
  }

  // Directional glow
  const directionalGlowColor = rand() > 0.5 ? palette.lightBlue : palette.blue;
  const glowX = Math.round(15 + rand() * 70);
  const glowY = Math.round(10 + rand() * 60);
  layers.push(
    `radial-gradient(circle at ${glowX}% ${glowY}%, ${rgba(
      directionalGlowColor,
      glowIntensity,
    )} 0%, transparent 55%)`,
  );

  // Base layer — subtle gradient so no white gaps remain
  const baseColor1 = rand() > 0.5 ? palette.blue : palette.lightBlue;
  const baseColor2 = rand() > 0.5 ? palette.lightBlue : palette.blue;
  layers.push(
    `linear-gradient(${Math.round(rand() * 360)}deg, ${rgba(baseColor1, 0.92)} 0%, ${rgba(
      baseColor2,
      0.85,
    )} 100%)`,
  );

  return layers.join(', ');
}

/**
 * Creates an animated background with moving blobs
 * @param {HTMLElement} container - Container element
 * @param {string} seedInput - Seed string for consistency
 * @param {object} options - Animation options
 */
export function createAnimatedBackground(container, seedInput, options = {}) {
  const {
    blobCountMin = 4,
    blobCountMax = 6,
    accentProbability = 0.55,
    animationDuration = 20, // seconds
    glowIntensity = 0.18,
    colors = null,
  } = options;

  const seedFactory = hashString(seedInput);
  const rand = mulberry32(seedFactory());

  // Default to blue/lightBlue only for animated backgrounds
  const animColors = colors || {
    main: [
      { value: palette.blue, weight: 6 },
      { value: palette.lightBlue, weight: 5 },
    ],
    accent: [
      { value: palette.red, weight: 3 },
      { value: palette.yellow, weight: 3 },
    ],
  };

  // Generate blob configurations
  const blobCount = blobCountMin + Math.floor(rand() * (blobCountMax - blobCountMin + 1));
  const accentCount = rand() > accentProbability ? 2 : 1;
  const blobs = [];

  for (let i = 0; i < blobCount; i++) {
    const isAccent = i >= blobCount - accentCount;
    const blob = generateBlob(rand, isAccent, animColors);
    // Add target position for animation
    blob.targetX = Math.round(rand() * 100);
    blob.targetY = Math.round(rand() * 100);
    blob.duration = animationDuration + rand() * 4; // Vary duration slightly
    blobs.push(blob);
  }

  // Clear any existing content
  container.innerHTML = '';

  // Create blob elements
  container.style.position = 'absolute';
  container.style.inset = '0';
  container.style.overflow = 'hidden';
  container.style.pointerEvents = 'none';
  blobs.forEach((blob, index) => {
    const blobEl = document.createElement('div');
    blobEl.className = 'animated-blob';
    blobEl.style.cssText = `
      position: absolute;
      width: ${blob.size * 2.8}%;
      height: ${blob.size * 2.8}%;
      left: ${blob.x}%;
      top: ${blob.y}%;
      background: radial-gradient(circle, ${rgba(blob.color, blob.alpha * 0.9)} 0%, ${rgba(
        blob.color,
        blob.alpha * 0.6,
      )} 32%, transparent 68%);
      border-radius: 50%;
      filter: blur(50px);
      animation: float-${index} ${blob.duration}s ease-in-out infinite;
      pointer-events: none;
      z-index: 0;
      transform: translate(-50%, -50%);
    `;

    // Create keyframe animation — smooth continuous loop
    // Clamp movement so blobs stay away from edges
    const dx = clamp((blob.targetX - blob.x) * 0.4, -20, 20);
    const dy = clamp((blob.targetY - blob.y) * 0.4, -20, 20);
    const keyframes = `
      @keyframes float-${index} {
        0% {
          transform: translate(-50%, -50%) translate(0, 0) scale(1);
        }
        25% {
          transform: translate(-50%, -50%) translate(${dx}%, ${-dy * 0.6}%) scale(1.12);
        }
        50% {
          transform: translate(-50%, -50%) translate(${dx * 1.2}%, ${dy}%) scale(0.95);
        }
        75% {
          transform: translate(-50%, -50%) translate(${-dx * 0.4}%, ${dy * 0.8}%) scale(1.08);
        }
        100% {
          transform: translate(-50%, -50%) translate(0, 0) scale(1);
        }
      }
    `;

    // Inject keyframes
    const styleSheet = document.createElement('style');
    styleSheet.textContent = keyframes;
    document.head.appendChild(styleSheet);

    container.appendChild(blobEl);
  });

  // Add glow layer
  const glowColor = rand() > 0.5 ? palette.lightBlue : palette.blue;
  const glowX = Math.round(15 + rand() * 70);
  const glowY = Math.round(10 + rand() * 60);
  const glowEl = document.createElement('div');
  glowEl.className = 'animated-glow';
  glowEl.style.cssText = `
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at ${glowX}% ${glowY}%, ${rgba(
      glowColor,
      glowIntensity,
    )} 0%, transparent 55%);
    pointer-events: none;
    z-index: 1;
  `;
  container.appendChild(glowEl);

  // Add white overlay
  const overlayEl = document.createElement('div');
  overlayEl.className = 'animated-overlay';
  overlayEl.style.cssText = `
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, ${rgba('#ffffff', 0.25)} 0%, ${rgba(
      '#ffffff',
      0.05,
    )} 32%, transparent 70%);
    pointer-events: none;
    z-index: 2;
  `;
  container.appendChild(overlayEl);
}

/**
 * Global export for non-module usage
 */
if (typeof window !== 'undefined') {
  window.TheyBackgroundGenerator = {
    palette,
    buildStaticBackground,
    createAnimatedBackground,
    // Utility functions
    mulberry32,
    hashString,
    clamp,
    hexToRgb,
    rgba,
    pickWeighted,
    generateBlob,
  };
}
